type UserLocation = {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  capturedAt: string;
};

const STORAGE_KEY = 'userLocation';
const MAX_LOCATION_AGE_MS = 5 * 60 * 1000;
let pendingLocation: Promise<UserLocation | null> | null = null;

const readCachedLocation = (): UserLocation | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as UserLocation;
    const capturedAt = new Date(parsed.capturedAt).getTime();
    if (!capturedAt || Date.now() - capturedAt > MAX_LOCATION_AGE_MS) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

const writeCachedLocation = (location: UserLocation) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
};

export const getCurrentUserLocation = async (): Promise<UserLocation | null> => {
  const cached = readCachedLocation();
  if (cached) return cached;

  if (pendingLocation) return pendingLocation;

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return null;
  }

  const locationRequest = new Promise<UserLocation | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: UserLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          capturedAt: new Date().toISOString(),
        };
        writeCachedLocation(location);
        resolve(location);
      },
      () => resolve(null),
      {
        enableHighAccuracy: false,
        maximumAge: MAX_LOCATION_AGE_MS,
        timeout: 3500,
      },
    );
  });

  pendingLocation = locationRequest;
  locationRequest.finally(() => {
    pendingLocation = null;
  });

  return locationRequest;
};
